import React from "react";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";

// Shared, consistently-styled replacements for the
// <FormControl><FormLabel/><TextField variant="standard"/></FormControl><br/>
// blocks that were copy-pasted across every viewEdit*.js form. Same
// props/behavior as a plain <TextField> (name/value/onChange/disabled all
// pass straight through untouched) — this only changes how it looks.

// Field name (label) and the actual value get distinct treatment: the
// label is a muted, medium-weight caption (turning primary blue only
// while focused), and the value itself is bold and dark so it's the part
// that visually stands out — the label's job is just to identify the
// field, not compete with the data in it. Disabled fields (view-only,
// i.e. before "Edit" is clicked) keep this same full-strength value
// color instead of MUI's default faded-grey disabled look, since here
// "disabled" means "read-only," not "unavailable."
const fieldSx = (sx) => ({
  mt: 0.75,
  mb: 2,
  "& .MuiInputLabel-root": {
    color: "text.secondary",
    fontWeight: 500,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "primary.main",
  },
  "& .MuiOutlinedInput-input": {
    color: "text.primary",
    fontWeight: 600,
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "currentColor",
    color: "text.primary",
    opacity: 1,
  },
  ...sx,
});

export function FormField({ label, sx, ...textFieldProps }) {
  return (
    <TextField
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      sx={fieldSx(sx)}
      {...textFieldProps}
    />
  );
}

// Same as FormField, but renders a dropdown (MUI's `select` mode on
// TextField) instead of a free-text input. `items` is a plain array of
// option values/labels.
export function FormSelectField({ label, items, sx, ...selectProps }) {
  return (
    <TextField
      select
      label={label}
      variant="outlined"
      size="small"
      fullWidth
      sx={fieldSx(sx)}
      {...selectProps}
    >
      {items.map((item) => (
        <MenuItem key={item} value={item}>
          {item}
        </MenuItem>
      ))}
    </TextField>
  );
}

// Button row footer for the viewEdit*.js forms — replaces a bare row of
// unstyled default <Button>s with consistent spacing/alignment.
export function FormActions({ children, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        mt: 1,
        pt: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
